/**
 * Work order pricing-adjustment (discount/surcharge) operations.
 *
 * Functions are invoked with `this` bound to the WorkOrdersService instance
 * (WorkOrdersCtx), like the sibling modules. Every mutation returns the
 * updated { adjustments, totals } envelope so callers can refresh state
 * from the response instead of relying on SSE.
 */
function asAdjustmentsResponse(data) {
    return {
        adjustments: Array.isArray(data?.adjustments) ? data.adjustments : [],
        totals: data?.totals,
    };
}
/** Applied adjustments + adjusted totals for a work order. Reads are open to account members. */
export async function getAdjustments(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/adjustments`);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to fetch adjustments');
        }
        return asAdjustmentsResponse(response.data);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get adjustments error:', error);
        throw error;
    }
}
/**
 * Apply an adjustment: either a manual template ({ rule_id }) or a custom
 * one ({ adjustment_type, method, value, reason? }). Requires pricing:apply.
 */
export async function addAdjustment(workOrderId, payload) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/adjustments`, payload);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to add adjustment');
        }
        return asAdjustmentsResponse(response.data);
    }
    catch (error) {
        console.error('[WorkOrdersService] Add adjustment error:', error);
        throw error;
    }
}
/**
 * Remove an applied adjustment (pricing:apply for manual entries,
 * pricing:manage for rule-sourced ones — the backend enforces).
 */
export async function removeAdjustment(workOrderId, adjustmentId) {
    try {
        const response = (await this.client.delete(`/work-orders/${workOrderId}/adjustments/${adjustmentId}`));
        if (!response.success) {
            throw new Error(response.error || 'Failed to remove adjustment');
        }
        // DELETE returns the updated envelope; fall back to a re-read if the
        // client's delete surface drops the body.
        if (response.data?.totals)
            return asAdjustmentsResponse(response.data);
        return getAdjustments.call(this, workOrderId);
    }
    catch (error) {
        console.error('[WorkOrdersService] Remove adjustment error:', error);
        throw error;
    }
}
/**
 * Preview what the auto-apply rules would add on close: matching rules with
 * computed amounts, plus the resulting totals. Advisory — callers showing
 * this before a close should catch and proceed on failure.
 */
export async function previewAdjustments(workOrderId) {
    const response = await this.client.get(`/work-orders/${workOrderId}/adjustments/preview`);
    if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to preview adjustments');
    }
    const data = response.data;
    return {
        rules_that_would_apply: Array.isArray(data.rules_that_would_apply) ? data.rules_that_would_apply : [],
        totals_after: data.totals_after,
    };
}
